import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, ExternalLink, Eye, GripVertical, ChevronUp, ChevronDown, Copy, Check, Link2, Search } from "lucide-react";
import AdminRecordDrawer from "@/pages/admin/AdminRecordDrawer";

const STATUS_TONE = {
  new: "bg-amber-50 text-amber-800 border-amber-200",
  in_review: "bg-sky-50 text-sky-800 border-sky-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  declined: "bg-rose-50 text-rose-800 border-rose-200",
  spam_review: "bg-orange-50 text-orange-800 border-orange-200",
};

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "new", label: "New" },
  { key: "in_review", label: "In review" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
  { key: "spam_review", label: "Auto-flagged" },
];

const NEXT_STATUSES = ["in_review", "approved", "declined"];

export default function AdminBrandApplicationsTab() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardBusy, setCardBusy] = useState("");
  const [dragId, setDragId] = useState(null);
  const [copiedId, setCopiedId] = useState("");

  const refLink = (code) =>
    `${typeof window !== "undefined" ? window.location.origin : ""}/ref/${code}`;

  const copyRef = async (a) => {
    try {
      await navigator.clipboard.writeText(refLink(a.referral_code));
      setCopiedId(a.id);
      setTimeout(() => setCopiedId(""), 1800);
    } catch { /* noop */ }
  };

  const loadCards = async () => {
    try {
      // Ensure approved ambassadors always have a (hidden) feature card so the
      // reorder/publish panel appears. Idempotent — safe to call on every load.
      try { await api.post("/admin/partner-cards/backfill"); } catch { /* noop */ }
      const { data } = await api.get("/admin/partner-cards");
      setCards(data.cards || []);
    } catch { /* noop */ }
  };

  const persistOrder = async (next) => {
    setCards(next);
    try {
      await api.post("/admin/partner-cards/reorder", { ordered_ids: next.map((c) => c.id) });
    } catch { await loadCards(); }
  };

  const moveCard = async (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= cards.length) return;
    const next = [...cards];
    const [moved] = next.splice(idx, 1);
    next.splice(to, 0, moved);
    await persistOrder(next);
  };

  const onDropCard = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = cards.map((c) => c.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const next = [...cards];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setCards(next);
    setDragId(null);
    try {
      await api.post("/admin/partner-cards/reorder", { ordered_ids: next.map((c) => c.id) });
    } catch { await loadCards(); }
  };

  const togglePublish = async (cardId, next) => {
    setCardBusy(cardId);
    try {
      await api.post(`/admin/partner-cards/${cardId}/publish`, { published: next });
      await loadCards();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not update card");
    } finally { setCardBusy(""); }
  };

  const load = async () => {
    setErr("");
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (query.trim()) params.set("q", query.trim());
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await api.get(`/admin/brand-partner-applications${qs}`);
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, query]);

  useEffect(() => { loadCards(); }, []);

  const advance = async (id, newStatus) => {
    setBusy(true);
    try {
      await api.post(`/admin/brand-partner-applications/${id}/status`, {
        status: newStatus,
      });
      await load();
      await loadCards();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p data-testid="brand-apps-loading">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="admin-brand-apps">
      <div className="flex items-center gap-3">
        <Sparkles size={22} className="text-[var(--ff-secondary)]" />
        <div>
          <p className="overline">Influencer & creator pipeline</p>
          <h2 className="font-display text-3xl">Brand Partner Applications</h2>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3" data-testid="brand-apps-counts">
        {Object.entries(data.counts || {}).map(([k, v]) => (
          <div
            key={k}
            className="card-flat p-4 text-center"
            data-testid={`brand-count-${k}`}
          >
            <p className="overline">{k.replace("_", " ")}</p>
            <p className="font-display text-3xl mt-1">{v}</p>
          </div>
        ))}
      </div>

      {cards.length > 0 && (
        <div className="card-flat p-5" data-testid="partner-cards-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="overline">Website feature cards</p>
              <h3 className="font-display text-xl">Tested, Tasted & Loved — publish control</h3>
            </div>
            <span className="text-xs text-[var(--ff-text-muted)]">{cards.filter(c => c.published).length} live · {cards.length} total</span>
          </div>
          <p className="text-xs text-[var(--ff-text-muted)] mb-3 -mt-2">Drag the <GripVertical size={11} className="inline" /> handle (or use the ↑↓ buttons on mobile) to reorder how ambassadors appear in the website carousel.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {cards.map((c, idx) => (
              <div
                key={c.id}
                draggable
                onDragStart={() => setDragId(c.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDropCard(c.id)}
                className={`flex items-center gap-3 border rounded-xl p-3 ${dragId === c.id ? "border-[var(--ff-primary)] opacity-60" : "border-[var(--ff-border)]"}`}
                data-testid={`partner-card-row-${c.id}`}
              >
                <div className="flex flex-col items-center shrink-0">
                  <GripVertical size={16} className="text-[var(--ff-text-muted)] cursor-grab hidden sm:block" data-testid={`partner-card-drag-${c.id}`} />
                  <button
                    onClick={() => moveCard(idx, -1)}
                    disabled={idx === 0}
                    className="p-0.5 rounded hover:bg-[var(--ff-muted)] disabled:opacity-30"
                    title="Move up"
                    data-testid={`partner-card-up-${c.id}`}
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    onClick={() => moveCard(idx, 1)}
                    disabled={idx === cards.length - 1}
                    className="p-0.5 rounded hover:bg-[var(--ff-muted)] disabled:opacity-30"
                    title="Move down"
                    data-testid={`partner-card-down-${c.id}`}
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>
                {c.photo
                  ? <img src={c.photo} alt={c.name} className="w-14 h-14 rounded-lg object-cover shrink-0" />
                  : <div className="w-14 h-14 rounded-lg bg-[var(--ff-muted)] shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{c.name}</p>
                  <p className="text-xs text-[var(--ff-text-muted)] truncate">{c.title}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] rounded-full ${c.published ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid={`partner-card-status-${c.id}`}>
                    {c.published ? "Live on site" : "Hidden"}
                  </span>
                </div>
                <button
                  onClick={() => togglePublish(c.id, !c.published)}
                  disabled={cardBusy === c.id}
                  className={`px-3 py-1.5 text-xs rounded-full font-semibold shrink-0 ${c.published ? "border border-[var(--ff-border)] hover:border-rose-400 hover:text-rose-600" : "bg-[var(--ff-primary)] text-white"}`}
                  data-testid={`partner-card-toggle-${c.id}`}
                >
                  {cardBusy === c.id ? "…" : c.published ? "Hide" : "Publish"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setStatus(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
              status === f.key
                ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]"
                : "bg-white text-[var(--ff-text-secondary)] border-[var(--ff-border)] hover:border-[var(--ff-primary)]"
            }`}
            data-testid={`brand-filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email or code…"
            className="w-full pl-9 pr-8 py-2 rounded-full text-sm border border-[var(--ff-border)] bg-white focus:outline-none focus:border-[var(--ff-primary)]"
            data-testid="brand-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ff-text-muted)] hover:text-[var(--ff-text-primary)]"
              data-testid="brand-search-clear"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {err && (
        <p className="text-sm text-rose-700" data-testid="brand-err">
          {err}
        </p>
      )}

      <div className="card-flat overflow-hidden">
        {(data.applications || []).length === 0 ? (
          <p className="p-8 text-center text-[var(--ff-text-muted)]">
            No applications yet.
          </p>
        ) : (
          <div className="divide-y divide-[var(--ff-border)]">
            {data.applications.map((a) => (
              <div key={a.id} className="p-5" data-testid={`brand-row-${a.id}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display text-xl">{a.full_name}</h3>
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full border ${
                          STATUS_TONE[a.status] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {a.status}
                      </span>
                      {a.honeypot_flagged && (
                        <span className="px-2 py-0.5 text-[10px] uppercase font-semibold tracking-wider rounded-full border bg-orange-50 text-orange-800 border-orange-200" title="Auto-flagged by the spam filter — review before approving. Likely a legit applicant whose browser autofilled a hidden field." data-testid={`brand-flagged-${a.id}`}>⚠ auto-flagged</span>
                      )}
                      <span className="text-xs text-[var(--ff-text-muted)] uppercase tracking-wider">
                        {a.partner_type}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--ff-text-secondary)] mt-1">
                      {a.email} · {a.city_state} · {new Date(a.created_at).toLocaleDateString()}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs">
                      {a.instagram && <span>IG {a.instagram}</span>}
                      {a.tiktok && <span>TikTok {a.tiktok}</span>}
                      {a.youtube && <span>YT {a.youtube}</span>}
                      {a.follower_count && <span>· {a.follower_count} followers</span>}
                    </div>
                    {a.referral_code && (
                      <div
                        className="mt-3 inline-flex items-center gap-2 flex-wrap rounded-xl border border-[var(--ff-border)] bg-[var(--ff-muted)] px-3 py-2"
                        data-testid={`brand-ref-${a.id}`}
                      >
                        <Link2 size={14} className="text-[var(--ff-primary)] shrink-0" />
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ff-text-muted)]">Referral</span>
                        <code className="text-xs font-mono text-[var(--ff-text-primary)]" data-testid={`brand-ref-code-${a.id}`}>
                          {a.referral_code}
                        </code>
                        <span className="hidden sm:inline text-xs font-mono text-[var(--ff-text-muted)] break-all">
                          {refLink(a.referral_code)}
                        </span>
                        <button
                          onClick={() => copyRef(a)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[var(--ff-primary)] text-white hover:opacity-90"
                          data-testid={`brand-ref-copy-${a.id}`}
                        >
                          {copiedId === a.id ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
                        </button>
                        {a.stripe_promo_code_id ? (
                          <span className="text-[11px] text-emerald-700" data-testid={`brand-ref-promo-${a.id}`}>Stripe promo active</span>
                        ) : (
                          <span className="text-[11px] text-amber-700">Stripe promo pending</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {NEXT_STATUSES.filter((s) => s !== a.status).map((s) => (
                      <button
                        key={s}
                        onClick={() => advance(a.id, s)}
                        disabled={busy}
                        className="px-3 py-1.5 text-xs rounded-full font-semibold border border-[var(--ff-border)] hover:border-[var(--ff-primary)] hover:text-[var(--ff-primary)]"
                        data-testid={`brand-advance-${a.id}-${s}`}
                      >
                        → {s.replace("_", " ")}
                      </button>
                    ))}
                    <button
                      onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                      className="text-xs text-[var(--ff-primary)] underline"
                      data-testid={`brand-toggle-${a.id}`}
                    >
                      {expanded === a.id ? "Hide" : "View"}
                    </button>
                    <button
                      onClick={() => setDetail(a)}
                      className="text-xs text-[var(--ff-primary)] underline inline-flex items-center gap-1"
                      data-testid={`brand-details-${a.id}`}
                    >
                      <Eye size={12}/> Full details
                    </button>
                  </div>
                </div>
                {expanded === a.id && (
                  <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm bg-[var(--ff-muted)] rounded-xl p-4">
                    <Field k="Primary audience">{a.primary_audience || "—"}</Field>
                    <Field k="Interests">
                      {(a.collab_interests || []).join(", ") || "—"}
                    </Field>
                    <Field k="Why partner" full>
                      {a.why_partner || "—"}
                    </Field>
                    <Field k="Prior brand experience" full>
                      {a.prior_brand_experience || "—"}
                    </Field>
                    <Field k="Website / media kit">
                      {a.website || a.media_kit_url ? (
                        <a
                          href={a.website || a.media_kit_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[var(--ff-primary)] underline inline-flex items-center gap-1"
                        >
                          Open <ExternalLink size={12} />
                        </a>
                      ) : (
                        "—"
                      )}
                    </Field>
                    <Field k="Additional notes" full>
                      {a.additional_notes || "—"}
                    </Field>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <AdminRecordDrawer record={detail} title={detail?.brand_name ? `${detail.brand_name} — brand application` : "Brand application"} onClose={() => setDetail(null)} />
    </div>
  );
}

function Field({ k, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--ff-text-muted)]">
        {k}
      </p>
      <p className="mt-1 text-[var(--ff-text-primary)]">{children}</p>
    </div>
  );
}
